<template>
  <div class="donation-flow">
    <v-slide-x-transition v-if="props.stepperReady" appear>
      <div class="donation-flow__stepper elevation-1">
        <progress-indicator-component
          v-if="currentStep.step > 2"
          :current-step="currentStep.step"
          :steps="availableSteps"
          :request-has-external-agent="requestHasExternalAgent"
          :request-has-only-one-case="requestHasOnlyOneCase"
        />

        <v-slide-x-transition mode="out-in">
          <div class="v-stepper-items">
            <div
              v-show="currentStep.step === 0"
              key="step-0"
              class="text-center step-content"
              data-cy="step0"
              step="0"
            >
              <v-progress-circular class="my-8" color="primary" indeterminate />
            </div>

            <div v-show="currentStep.step === 1" key="step-1" data-cy="step1" step="1">
              <donation-type-view
                v-if="requestSession"
                :donation-types="requestSession.donationTypes"
                :compact="true"
                @add-step="addStep"
                @next-step="nextStep"
                @remove-step="removeStep"
                @select-donation-type="onDonationTypeSelected"
              />
            </div>
            <div v-show="currentStep.step === 2" key="step-2" data-cy="step2" step="2">
              <information-view
                :request-has-external-agent="requestHasExternalAgent"
                :request-has-organization-group="requestHasOrganizationGroup"
                :show-previous-button="multipleDonationTypes"
                :compact="true"
                @add-step="addStep"
                @next-step="nextStep"
                @previous-step="previousStep"
                @remove-step="removeStep"
              />
            </div>
            <div v-show="currentStep.step === 3" key="step-3" data-cy="step3" step="3">
              <case-view
                compact
                :request-has-external-agent="requestHasExternalAgent"
                :show-prev="requestHasOrganizationGroup || !requestHasExternalAgent"
                :request-has-organization-group="requestHasOrganizationGroup"
                @add-step="addStep"
                @next-step="nextStep"
                @previous-step="previousStep"
                @remove-step="removeStep"
              />
            </div>
            <div
              v-show="currentStep.step === 4"
              key="step-4"
              class="step-content"
              data-cy="step4"
              step="4"
            >
              <certificate-details-view
                :show-prev="true"
                @add-step="addStep"
                @next-step="nextStep"
                @previous-step="previousStep"
                @remove-step="removeStep"
              />
            </div>
            <div
              v-show="currentStep.step === 5"
              key="step-5"
              class="step-content"
              data-cy="step5"
              step="5"
            >
              <donation-details-and-payment-delivery-view
                @next-step="nextStep"
                @previous-step="previousStep"
              />
            </div>
            <div v-show="currentStep.step === 6" key="step-6" data-cy="step6" step="6">
              <summary-view
                compact
                @next-step="nextStep"
                @previous-step="previousStep"
                @step="setStep"
              />
            </div>
            <div
              v-show="currentStep.step === 7"
              key="step-7"
              class="step-content"
              data-cy="step7"
              step="7"
            >
              <payment-view compact disable-elevation @reset="reset" @step="setStep" />
            </div>
            <div
              v-show="currentStep.step === 8"
              key="step-8"
              class="step-content"
              data-cy="step8"
              step="8"
            >
              <success-view compact disable-elevation @reset="reset" />
            </div>
            <div
              v-show="currentStep.step === 9"
              key="step-9"
              class="step-content"
              data-cy="step9"
              step="9"
            >
              <paylink-payment-view compact disable-elevation @step="setStep" />
            </div>
          </div>
        </v-slide-x-transition>
      </div>
    </v-slide-x-transition>

    <logo-footer v-if="props.stepperReady" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { DonationStep } from '@/interfaces/donationStep';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import { DonationType, Type } from '@/classes/DonationType';
import { getDonationStep, getDonationSteps } from '@/utils/donationStepUtils';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';

import { useDonationReset } from '@/composables/useDonationReset';
import store from '@/store';

import DonationTypeView from '@/views/DonationTypeView.vue';
import InformationView from '@/views/InformationView.vue';
import CaseView from '@/views/CaseView.vue';
import CertificateDetailsView from '@/views/CertificateDetails.vue';
import SummaryView from '@/views/SummaryView.vue';
import PaymentView from '@/views/PaymentView.vue';
import PaylinkPaymentView from '@/views/PaylinkPaymentView.vue';
import SuccessView from '@/views/SuccessView.vue';

import LogoFooter from '@/components/global/LogoFooter.vue';
import ProgressIndicatorComponent from '../progressIndicator/ProgressIndicatorComponent.vue';
import DonationDetailsAndPaymentDeliveryView from '@/views/DonationDetailsAndPaymentDeliveryView.vue';

const props = defineProps<{
  multipleDonationTypes?: boolean;
  requestHasExternalAgent?: boolean;
  requestHasOnlyOneCase?: boolean;
  requestHasOrganizationGroup?: boolean;
  loading?: boolean;
  definedStep?: number | null;
  stepperReady?: boolean;
}>();

// Data
const availableSteps = ref([] as DonationStep[]);
const defaultDonationGuideVersion = ref(DonationGuideVersion.V2);

// Methods from mixin (defined early)
const getDonationStepFlowVersion = (): DonationGuideVersion => {
  return store.state.donationGuideVersion || defaultDonationGuideVersion.value;
};

const getDonationStepLocal = (stepNumber: number): DonationStep | undefined => {
  return getDonationStep(stepNumber, getDonationStepFlowVersion());
};

const getInitialSteps = (): DonationStep[] => {
  return [...getDonationSteps(getDonationStepFlowVersion())];
};

const setDonationGuideVersion = (donationGuideVersion: DonationGuideVersion) => {
  store.commit('setDonationGuideVersion', donationGuideVersion);
};

const resetSteps = () => {
  availableSteps.value = getDonationSteps(getDonationStepFlowVersion());
};

const onDonationTypeSelected = (donationType: DonationType) => {
  if (!donationType || !donationType.type) {
    window.log?.error('[Guide] donationType.invalid — Invalid donation type selected', {
      donationTypeId: donationType?.id,
    });
    return;
  }
  const donationPayload = {
    donationType,
    donor: {
      address: { address1: '', address2: '', address3: '', city: '', country: '', postcode: '' },
      email: '',
      firstname: '',
      surname: '',
      socialSecurityNumber: '',
      token: '',
    },
    attributeValues: [],
    paymentSpecification: null,
    deliveryMethod: null,
    donationCause: null,
    donationImageId: null,
    sharePersonalInfoWithOrganization: true,
  } as any;
  store.commit('updateDonation', donationPayload);
  const version =
    donationType.type === Type.CONTRIBUTION ? DonationGuideVersion.V1 : DonationGuideVersion.V2;
  setDonationGuideVersion(version);
  resetSteps();
  nextTick(() => {
    const stepsVal = version === DonationGuideVersion.V2 ? stepV2 : stepV1;
    const newStep: number =
      donationType.type === Type.CONTRIBUTION ? stepV1.DONOR : stepsVal.INFORMATION;
    setStep(newStep);
  });
};

const previousStep = () => {
  const currentStepNumber: number = (currentStep.value as DonationStep).step;
  const previousSteps = availableSteps.value
    .map((step: DonationStep) => step.step)
    .filter((stepNumber: number) => stepNumber < currentStepNumber)
    .sort((a: number, b: number) => b - a);

  if (previousSteps.length > 0) {
    store.commit('updateCurrentStep', previousSteps[0]);
    nextTick(() => {
      scrollToTopInstant();
    });
  }
};

const nextStep = () => {
  const currentStepNumber: number = (currentStep.value as DonationStep).step;

  const nextSteps = availableSteps.value
    .map((step: DonationStep) => step.step)
    .filter((stepNumber: number) => stepNumber > currentStepNumber)
    .sort((a: number, b: number) => a - b);

  if (nextSteps.length > 0) {
    nextTick(() => {});
    scrollToTopInstant();
    store.commit('updateCurrentStep', nextSteps[0]);
  }
};

const removeStep = (stepToRemove: number) => {
  const index = availableSteps.value.findIndex((val: DonationStep) => val.step === stepToRemove);
  if (index >= 0) {
    availableSteps.value.splice(index, 1);
  }
};

const addStep = (stepToAdd: number) => {
  let guideStep = availableSteps.value.find((val: DonationStep) => val.step === stepToAdd);
  if (!guideStep) {
    guideStep = getDonationStepLocal(stepToAdd);
    if (guideStep) {
      availableSteps.value.push(guideStep);
      availableSteps.value.sort((a, b) => a.step - b.step);
    }
  }
};

const setStep = (step: number) => {
  store.commit('updateCurrentStep', step);
  nextTick(() => {});
};

const scrollToTopInstant = () => {
  window.scrollTo({
    top: 0,
    behavior: 'auto',
  });
};

const { resetDonation } = useDonationReset();

const reset = async () => {
  await resetDonation(donation.value);

  setStep(availableSteps.value[1].step);
};

// Computed
const requestSession = computed(() => store.state.requestSession);
const donation = computed(() => store.state.donation);
const currentStep = computed<DonationStep>({
  get: () =>
    getDonationStepLocal(store.state.currentStep) ||
    getDonationStepLocal(getInitialSteps()[0].step) ||
    ({ step: store.state.currentStep } as DonationStep),
  set: (val) => {
    const stepNumber = typeof val === 'number' ? val : (val?.step ?? store.state.currentStep);
    store.commit('updateCurrentStep', stepNumber);
  },
});
// dialog/drawer state available on store if needed

// Watch
watch(
  () => store.state.donationGuideVersion,
  () => {
    resetSteps();
  }
);

// Created
availableSteps.value = getInitialSteps();

// Watch for definedStep changes
watch(
  () => props.definedStep,
  (newStep) => {
    if (newStep !== null && newStep !== undefined) {
      setStep(newStep);
    }
  }
);

// Mounted
onMounted(() => {
  if (props.definedStep) {
    setStep(props.definedStep);
  }
});
</script>
<style lang="scss">
@import '../../assets/donation-flow/styles.scss';
</style>
